import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Chip, ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary, Tooltip, Typography } from '@material-ui/core';
import cx from 'classnames';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { translate } from '../../../../common/intl';
import { Approval, ApprovalStatus } from '../../../../state/ducks/common/types';
import { companySelectors } from '../../../../state/ducks/company';
import { Employee } from '../../../../state/ducks/company/types';
import { documentRevisionsActions } from '../../../../state/ducks/documentRevisions';
import { APPROVAL_STAGE_STATUS, ApprovalStage, WorkflowStatusDetails } from '../../../administration/general.settings/types';
import { FBAutocomplete } from '../../../form.builder';
import useActionCreator from '../../../hooks/useActionCreator';
import useAsync from '../../../hooks/useAsync';
import { Colors } from '../../../layout/theme-next';
import { Button, IconButton } from '../../forms/fields-next';
import { toastError, toastInfo } from '../../notifications';
import useStyles from './approvalWorkflow.styles';
import { getRequiredCountLabel, getWorkflowStageStatus, getWorkflowStageStatusText } from './helpers';
import { WorkflowStageStatus } from './types';
import WorkflowApprover from './WorkflowApprover';

interface WorkflowStageProps {
  changeRequestId?: string
  documentRevisionId?: string
  stage: ApprovalStage
  approvals: Approval[] | undefined
  currentEmpId: string
  isOwner: boolean
  isArtifactInReview?: boolean
  artifactHasMappedFields?: boolean
  workflowStatusDetails: WorkflowStatusDetails
  expandedStages: Set<number>
  disabled?: boolean
  toggleStage: (stageNumber: number) => void
  onApproveClick?: (approvalId: string) => void
  onRejectClick?: (approvalId: string) => void
}

const WorkflowStage: React.FC<WorkflowStageProps> = ({
  changeRequestId,
  documentRevisionId,
  stage,
  approvals,
  currentEmpId,
  isOwner,
  isArtifactInReview,
  artifactHasMappedFields,
  workflowStatusDetails,
  expandedStages,
  disabled,
  toggleStage,
  onApproveClick,
  onRejectClick,
}) => {
  const classes = useStyles();

  const activeStageNumber = workflowStatusDetails?.currentStageInProgress ?? 1;
  const isActiveStage = activeStageNumber === stage.stageNumber;

  // Sorted approvers list to bring the current employee first, for quick review.
  const stageApprovals = React.useMemo(() => {
    const validApprovals = approvals?.filter(
      approval => approval.stageNumber === stage.stageNumber && approval.status !== ApprovalStatus.Abandoned) ?? [];
    const sortedApprovers = validApprovals
      .sort((a, b) => {
        if (a.approverId === currentEmpId) { return -1; }
        if (b.approverId === currentEmpId) { return 1; }

        if (a.isOptional !== b.isOptional) {
          return a.isOptional ? 1 : -1;
        }

        if (a.approverName && b.approverName) {
          return a.approverName.localeCompare(b.approverName);
        }

        return 0;
      });
    return sortedApprovers;
  }, [
    approvals,
    stage.stageNumber,
    currentEmpId,
  ]);

  const hasOptionalApprover = stageApprovals?.some(approver => approver.isOptional);

  const isNotStarted = stage.status === APPROVAL_STAGE_STATUS.NOT_STARTED;
  const isInReview = stage.status === APPROVAL_STAGE_STATUS.IN_PROGRESS;
  const isCompleted = stage.status === APPROVAL_STAGE_STATUS.COMPLETED;

  // Stage based approval exists only for docRevs
  const canSendStageForApproval
    = documentRevisionId && isActiveStage && isNotStarted && isOwner && artifactHasMappedFields && !isArtifactInReview;
  const canWithdrawStage
    = documentRevisionId && (isInReview || isCompleted) && isOwner && artifactHasMappedFields && !isArtifactInReview && !disabled;

  const stageStatus = getWorkflowStageStatus(stage.stageNumber, activeStageNumber, isInReview);
  const stageStatusText = getWorkflowStageStatusText(stage.stageNumber, activeStageNumber, isInReview);

  const isStageOwnerEditable
    = stage.stageNumber > activeStageNumber && stage.stageNumber > 1;

  const allEmployees: Employee[] = useSelector(companySelectors.getActiveEmployees) as Employee[];
  const availableApprovers = allEmployees.map(
    employee => ({
      id: employee.id,
      name: employee.user?.name,
    }),
  );

  const getStageIcon = (
    stageStatus: WorkflowStageStatus,
  ) => {
    switch (stageStatus) {
      case WorkflowStageStatus.COMPLETED:
        return (
          <FontAwesomeIcon
            icon={solid('circle-check')}
            color={Colors.success}
          />
        );

      case WorkflowStageStatus.IN_PROGRESS:
        return (
          <FontAwesomeIcon
            icon={solid('hourglass-half')}
            color={Colors.primary}
          />
        );

      case WorkflowStageStatus.PENDING:
        return (
          <FontAwesomeIcon
            icon={regular('circle')}
            color={Colors.textDisabled}
          />
        );

      case WorkflowStageStatus.IN_REVIEW:
        return (
          <FontAwesomeIcon
            icon={solid('clipboard-check')}
            color={Colors.warning}
            fontSize={18}
          />
        );
    }
  };

  const requiredApprovers = stageApprovals.filter(stageApproval => !stageApproval.isOptional);

  const [isUpdatingOwner, setIsUpdatingOwner] = useState<boolean>(false);
  const updateStageOwnerAction = useActionCreator(documentRevisionsActions.updateStageOwner);
  const updateStageOwnerAsync = useAsync({
    onError: (error) => {
      toastError(error);
      setIsUpdatingOwner(false);
    },
    onSuccess: () => {
      toastInfo(translate('approval.configuration.workflow.stage.owner.updated.success.msg', { stageNumber: stage.stageNumber }));
      setIsUpdatingOwner(false);
    },
  });
  const onStageOwnerUpdated = (
    event: any, employeeId: string,
  ) => {
    event.stopPropagation();
    event.preventDefault();
    if (employeeId && !isUpdatingOwner) {
      setIsUpdatingOwner(true);
      updateStageOwnerAsync.start(
        updateStageOwnerAction,
        documentRevisionId,
        stage.id,
        employeeId ?? currentEmpId,
        updateStageOwnerAsync,
      );
    }
  };

  const [isSubmittingStage, setIsSubmittingStage] = useState<boolean>(false);
  const submitStageAction = useActionCreator(documentRevisionsActions.submitStageForApproval);
  const submitStageAsync = useAsync({
    onError: (error) => {
      toastError(error);
      setIsSubmittingStage(false);
    },
  });

  const submitStageForApproval = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, stageNumber: number) => {
    event.stopPropagation();
    event.preventDefault();
    if (isSubmittingStage) {
      return;
    }
    setIsSubmittingStage(true);
    submitStageAsync.start(
      submitStageAction,
      documentRevisionId,
      stageNumber,
      submitStageAsync,
    );
  };

  const [isWithdrawingStage, setIsWithdrawingStage] = useState<boolean>(false);
  const withdrawStageAction = useActionCreator(documentRevisionsActions.withdrawStageForApproval);
  const withdrawStageAsync = useAsync({
    onError: (error) => {
      toastError(error);
      setIsWithdrawingStage(false);
    },
  });

  const withdrawStageForApproval = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, stageNumber: number) => {
    event.stopPropagation();
    event.preventDefault();
    if (isWithdrawingStage) {
      return;
    }
    setIsWithdrawingStage(true);
    withdrawStageAsync.start(
      withdrawStageAction,
      documentRevisionId,
      stageNumber,
      withdrawStageAsync,
    );
  };

  return (
    <ExpansionPanel
      expanded={expandedStages.has(stage.stageNumber)}
      className={classes.stagePanel}
    >
      <ExpansionPanelSummary
        onClick={e => toggleStage(stage.stageNumber)}
        expandIcon={(
          <IconButton
            kind="secondary"
            size="small"
            iconSize={12}
            className={classes.iconButton}
          >
            <FontAwesomeIcon icon={regular('chevron-down')} />
          </IconButton>
        )}
      >
        <div className={classes.summaryContainer}>
          <div className={classes.leftSection}>
            <div className={classes.stageIcon}>
              {getStageIcon(stageStatus)}
            </div>

            <div className={classes.textSection}>
              <div className={classes.leftSection}>
                <Typography
                  variant="h2"
                  className={classes.title}
                >
                  {stage.name}
                </Typography>
                { hasOptionalApprover && <Tooltip
                  title={translate(
                    stage.optionalApproverRejectionRejectsStage
                      ? 'approval.configuration.workflow.optional.approver.rejection.rejects'
                      : 'approval.configuration.workflow.optional.approver.rejection.is.recorded',
                  )}
                  placement="right"
                  classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }}
                  arrow>
                  {stage.optionalApproverRejectionRejectsStage
                    ? <FontAwesomeIcon icon={solid('warning')} color={Colors.warning} className={classes.optionalRejectionIcon} />
                    : <FontAwesomeIcon icon={solid('info-circle')} color={Colors.primaryGradient} className={classes.optionalRejectionIcon} />
                  }
                </Tooltip> }
              </div>

              <Typography
                variant="body2"
                className={classes.subtitle}
              >
                {getRequiredCountLabel(stage.requiredCount)}
                {' • '}
                {`${requiredApprovers.length} Approver${requiredApprovers.length > 1 ? 's' : ''}`}
              </Typography>
            </div>

            {stage.editableFields?.length ? (
              <Chip
                className={classes.editableFieldsChip}
                size="small"
                label={`${stage.editableFields.length} Editable ${
                  stage.editableFields.length === 1 ? 'Field' : 'Fields'
                }`}
              />
            ) : <></>}
          </div>

          {
            stage.stageNumber > 1 && artifactHasMappedFields && stage.stageOwnerId
              ? <div className={classes.stageOwnerContainer}
                onClick={e => e.stopPropagation()}
              >
                <FBAutocomplete
                  placeholder="approval.configuration.workflow.stage.owner.autocomplete.placeholder"
                  // label={translate('approval.configuration.workflow.stage.owner.autocomplete.placeholder')}
                  options={availableApprovers}
                  optionLabelKey="name"
                  optionValueKey="id"
                  gutter={false}
                  omitFormValue={true}
                  onChange={(e, value) => {
                    onStageOwnerUpdated(e, value?.id);
                  }}
                  disabled={!isStageOwnerEditable}
                  groupByKey="N/A"
                  defaultValue={stage.stageOwnerId}
                />
              </div> : <></>
          }

          <Box className={classes.stageStatusContainer}>
            <Typography
              variant="body2"
              className={cx(classes.stageStatus, classes[stageStatus])}
            >
              {stageStatusText}
            </Typography>

            {canSendStageForApproval ? (
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => stage.id && submitStageForApproval(e, stage.id)}
              >
                {isSubmittingStage
                  ? translate('approval.configuration.workflow.stage.sending.approval.btn')
                  : translate('approval.configuration.workflow.stage.send.approval.btn')}
              </Button>
            ) : <></>}

            {canWithdrawStage ? (
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => stage.id && withdrawStageForApproval(e, stage.id)}
                className={classes.rejectBtn}
              >
                {isWithdrawingStage
                  ? translate('approval.configuration.workflow.stage.withdrawing.stage.btn')
                  : translate('approval.configuration.workflow.stage.withdraw.stage.btn')}
              </Button>
            ) : <></>}
          </Box>
        </div>
      </ExpansionPanelSummary>

      <ExpansionPanelDetails
        className={classes.details}
      >
        <Box className={classes.approverContainer}>
          {
            stageApprovals.map((approval) => {
              const hasReviewed = approval && approval.status !== ApprovalStatus.Pending;
              const isCurrentUser = approval.approverId === currentEmpId;
              const canReview = Boolean(approval)
                && isCurrentUser && stage.stageNumber === workflowStatusDetails?.currentStageInProgress
                && stage.status !== APPROVAL_STAGE_STATUS.COMPLETED;
              return (
                <WorkflowApprover
                  key={approval.approverId}
                  approval={approval}
                  isCurrentUser={isCurrentUser}
                  canReview={canReview}
                  hasReviewed={hasReviewed}
                  isStageCompleted={isCompleted}
                  stageNumber={stage.stageNumber}
                  changeRequestId={changeRequestId}
                  documentRevisionId={documentRevisionId}
                  onApproveClick={onApproveClick}
                  onRejectClick={onRejectClick}
                />
              );
            })
          }
        </Box>

      </ExpansionPanelDetails>
    </ExpansionPanel>
  );
};

export default WorkflowStage;
